import type { DependsOnType } from '@unifyapps/defs/types/page';
import type { BuildDependencyGraphItem } from '../utils/buildDependencyGraph';
import type { EntityDependencyNode, EntityId } from './types';

export class DependencyGraphManager {
  // a node corresponds to an entity, and it stores the dependencies of that entity
  private nodes = new Map<EntityId, EntityDependencyNode>();
  private cache = new Map<EntityId, Set<EntityId>>();

  /**
   * Add a node to the graph if it doesn't already exist.
   * @param id - The unique identifier of the node to add.
   */
  private invalidateCache(id: EntityId): void {
    this.cache.delete(id);
  }

  createNode(id: EntityId): void {
    if (!this.nodes.has(id)) {
      this.nodes.set(id, {
        _affectedBy: new Set(),
        _impacts: new Set(),
        dependsOn: [],
      });
      this.invalidateCache(id);
    }
  }

  /**
   * Add multiple nodes to the graph in bulk.
   * @param ids - An array of node identifiers to add.
   */
  createNodes(ids: EntityId[]): void {
    const idsLength = ids.length;
    for (let i = 0; i < idsLength; i++) {
      this.createNode(ids[i]);
    }
  }

  /**
   * Add a dependency relationship between two nodes.
   * @param nodeId - The node that depends on another node.
   * @param dependsOnId - The node being depended upon.
   */
  private addDependency(nodeId: EntityId, dependsOnId: EntityId): void {
    const node = this.nodes.get(nodeId);
    let dependsOnNode = this.nodes.get(dependsOnId);

    if (!node) {
      throw new Error(
        `Dependent node not found, before adding any dependency, create the dependent node ${nodeId}`,
      );
    }

    if (!dependsOnNode) {
      this.createNode(dependsOnId);
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- creating on a line above
      dependsOnNode = this.nodes.get(dependsOnId)!;
    }

    node._affectedBy.add(dependsOnId);
    dependsOnNode._impacts.add(nodeId);

    // Invalidate cache for both nodes and their dependents
    this.invalidateCache(nodeId);
    this.invalidateCache(dependsOnId);
  }

  /**
   * Update the items of a node's dependency.
   * @param id - The identifier of the node to update.
   * @param dependsOn - The dependency items to update.
   */
  updateNode(id: EntityId, dependsOn?: DependsOnType[]): void {
    let node = this.nodes.get(id);

    if (!dependsOn) {
      return;
    }

    // it is mandatory to have a node here
    if (!node) {
      this.createNode(id);
    }

    node = this.nodes.get(id);

    if (!node) {
      throw new Error('Node not found');
    }

    // remove myself from all other nodes where I am present as _impacts, because dependsOn will make new impacts entries
    for (const dependsOnItem of node._affectedBy) {
      const dependsOnNode = this.nodes.get(dependsOnItem);
      if (dependsOnNode) {
        dependsOnNode._impacts.delete(id);
      }
    }

    node._affectedBy.clear();

    const dependsOnLength = dependsOn.length;
    for (let i = 0; i < dependsOnLength; i++) {
      this.addDependency(id, dependsOn[i].id);
    }

    node.dependsOn = dependsOn;

    this.invalidateCache(id);
  }

  /**
   * Update dependency items for multiple nodes.
   * @param items - A record of node IDs and their corresponding dependency items.
   */
  updateNodes(items: Record<EntityId, BuildDependencyGraphItem<unknown> | undefined>): void {
    const itemKeys = Object.keys(items);
    const itemKeysLength = itemKeys.length;
    for (let i = 0; i < itemKeysLength; i++) {
      const id = itemKeys[i];
      const item = items[id];
      if (!item?.dependsOn) {
        console.error('No dependsOn found for id', id);
        continue;
      }
      this.updateNode(id, item.dependsOn);
    }
  }

  /**
   * Merges the new dependsOn with the existing dependsOn of the node.
   * @param id - The identifier of the node to update.
   * @param dependsOn - The dependency items to merge.
   */
  mergeNode(id: EntityId, dependsOn?: DependsOnType[]): void {
    let node = this.nodes.get(id);

    if (!dependsOn) {
      return;
    }

    if (!node) {
      this.createNode(id);
    }

    node = this.nodes.get(id);

    if (!node) {
      throw new Error('Node not found');
    }

    const mergedDependsOn = [...node.dependsOn];

    const dependsOnLength = dependsOn.length;
    for (let i = 0; i < dependsOnLength; i++) {
      const dependsOnItem = dependsOn[i];
      let existingDependsOnIndex = -1;
      for (let j = 0; j < mergedDependsOn.length; j++) {
        if (mergedDependsOn[j].id === dependsOnItem.id) {
          existingDependsOnIndex = j;
          break;
        }
      }
      if (existingDependsOnIndex === -1) {
        mergedDependsOn.push({
          id: dependsOnItem.id,
          p: [...new Set(dependsOnItem.p)],
        });
      } else {
        const existingDependsOnItem = mergedDependsOn[existingDependsOnIndex];
        const newPath = [...new Set([...existingDependsOnItem.p, ...dependsOnItem.p])];
        mergedDependsOn[existingDependsOnIndex] = {
          id: dependsOnItem.id,
          p: newPath,
        };
      }
    }

    this.updateNode(id, mergedDependsOn);
  }

  /**
   * Get all impacted nodes when a node changes, including transitive dependencies.
   * Results are cached for improved performance.
   * @param changedId - The identifier of the node that has changed.
   * @returns A set of all impacted node identifiers.
   */
  getImpactedNodes(changedId: EntityId): Set<EntityId> {
    // Check cache first
    const cachedResult = this.cache.get(changedId);
    if (cachedResult) {
      return cachedResult;
    }

    // If not in cache, compute the result
    const affected = new Set<EntityId>();
    const queue: EntityId[] = [changedId];

    while (queue.length > 0) {
      const currentId = queue.shift();
      if (!currentId) continue;
      const node = this.nodes.get(currentId);

      if (!node) continue;
      affected.add(currentId);

      // Add all the impacts of the current node to the queue
      for (const dependentId of node._impacts) {
        if (!affected.has(dependentId)) {
          queue.push(dependentId);
        }
      }
    }

    // Cache the result before returning
    this.cache.set(changedId, affected);
    return affected;
  }

  getNonTransitiveImpactedNode(changedId: EntityId): Set<EntityId> {
    const impactedNodes = this.nodes.get(changedId)?._impacts;
    return impactedNodes || new Set();
  }

  /**
   * Delete a node from the graph and update dependencies.
   * @param id - The identifier of the node to delete.
   */
  deleteNode(id: EntityId): void {
    const node = this.nodes.get(id);
    if (!node) return;

    // Invalidate cache for the node and all its relationships
    this.invalidateCache(id);

    for (const dependencyId of node._affectedBy) {
      const dependency = this.nodes.get(dependencyId);
      if (dependency) {
        dependency._impacts.delete(id);
        this.invalidateCache(dependencyId);
      }
    }

    for (const dependentId of node._impacts) {
      const dependent = this.nodes.get(dependentId);
      if (dependent) {
        dependent._affectedBy.delete(id);
        this.invalidateCache(dependentId);
      }
    }
    this.nodes.delete(id);
  }

  /**
   * Retrieve a node from the graph.
   * @param id - The identifier of the node to retrieve.
   * @returns The node if found, otherwise null.
   */
  getNode(id?: EntityId | null): EntityDependencyNode | null {
    if (!id) return null;
    return this.nodes.get(id) || null;
  }

  getNodes() {
    return this.nodes;
  }

  /**
   * Reset the graph by clearing all nodes.
   * This should be called when the graph is no longer needed.
   */
  reset(): void {
    this.nodes.clear();
    this.cache.clear();
  }
}
